/**
 * Farm & Acre Co — Allflex CRO dynamic savings (Round 5 Task C.2, 2026-06-02)
 *
 * Two responsibilities, both quantity-aware:
 *
 *  1. Keep the top strikethrough block (.cro-savings-block) in sync with the
 *     currently displayed tier price. variant-auto-select.js swaps variants
 *     when the customer changes quantity; the top price element updates but
 *     the cro-savings render is server-side Liquid that stays at the initial
 *     variant's math. This script observes the price element and rewrites
 *     the .cro-savings-block__save + .cro-savings-block__pct text + the
 *     <s> compare value as price/quantity tier changes.
 *
 *  2. Add a savings line to the Price Breakdown box rendered by
 *     dynamic-pricing.js. We listen for the `pricing:updated` event the
 *     allflex-pricing web component fires (line 416-420 of dynamic-pricing.js)
 *     and inject/update a single .allflex-pricing__note--cro-savings note
 *     showing the total saving at the customer's current quantity.
 *
 * Opt-in: only runs when window.CRO_ALLFLEX_COMPARE is set (number, dollars).
 * That global is emitted by an inline <script> in allflex-product.liquid
 * conditional on product.metafields.custom.cro_compare_override > 0, so this
 * script is a no-op on RapID / Tamperproof / visual tag / FOFIA / Leader /
 * chainsaw products.
 *
 * Reads displayed prices from the DOM (already validated by Shopify variant
 * swap) — never invents tier math.
 */
(function () {
  'use strict';

  function init() {
    // Shopify's `| json` filter on number_decimal metafields outputs a string
    // ("6.66"), not a number. Coerce here so both shapes work.
    var compareRaw = window.CRO_ALLFLEX_COMPARE;
    var compare = typeof compareRaw === 'number' ? compareRaw : parseFloat(compareRaw);
    if (!compare || !isFinite(compare) || compare <= 0) return;

    var priceContainer = document.querySelector('[id^="price-"]');
    var savingsBlock = document.querySelector('.cro-savings-block');
    if (!priceContainer) return;

    // The .cro-savings-block we mutate lives inside priceContainer too, so
    // observing priceContainer with subtree:true loops forever — every text
    // write fires the observer, which fires updateStrikethroughBlock, which
    // writes again. Narrow the watched target to ONLY the price element
    // (sibling of the savings block) so our own mutations don't echo back.
    var observerTarget = priceContainer.querySelector('.price') || priceContainer;
    var lastSeenPrice = null;

    function parsePrice(text) {
      if (!text) return null;
      var m = String(text).replace(/,/g, '').match(/([0-9]+(?:\.[0-9]+)?)/);
      return m ? parseFloat(m[1]) : null;
    }

    function getCurrentPrice() {
      // Standard Shopify renders .price-item--regular or .price-item--sale
      // inside the price container; variant-auto-select.js mutates the text
      // when the variant swaps.
      var el = priceContainer.querySelector('.price-item--sale')
            || priceContainer.querySelector('.price-item--regular')
            || priceContainer.querySelector('.price-item');
      if (!el) return null;
      return parsePrice(el.textContent);
    }

    function fmt(n) {
      if (typeof n !== 'number' || !isFinite(n)) return '$0.00';
      return '$' + n.toFixed(2);
    }

    function updateStrikethroughBlock() {
      if (!savingsBlock) return;
      var price = getCurrentPrice();
      if (price === null) return;
      // Belt-and-braces against any re-entrancy: skip DOM writes when the
      // price hasn't changed since the last call. Loop-safe even if some
      // future change widens the observer target back to the container.
      if (price === lastSeenPrice) return;
      lastSeenPrice = price;
      if (price >= compare) {
        // If our price is at or above the compare, hide the block (matches
        // cro-savings.liquid's `if cro_compare > cro_price` guard).
        savingsBlock.style.display = 'none';
        return;
      }
      savingsBlock.style.display = '';

      var saving = compare - price;
      var pct = Math.round((saving / compare) * 100);

      var compareEl = savingsBlock.querySelector('.cro-savings-block__compare s');
      var saveEl    = savingsBlock.querySelector('.cro-savings-block__save');
      var pctEl     = savingsBlock.querySelector('.cro-savings-block__pct');

      if (compareEl) compareEl.textContent = fmt(compare);
      if (saveEl)    saveEl.textContent    = 'Save ' + fmt(saving);
      if (pctEl)     pctEl.textContent     = '(' + pct + '% off)';

      // Keep the analytics dataset in sync with the displayed values so
      // any downstream listener sees correct numbers.
      savingsBlock.dataset.savingsAmount = (saving * 100).toFixed(0);
      savingsBlock.dataset.savingsPct = String(pct);
    }

    function injectBreakdownSavings(detail) {
      var pricingEl = document.querySelector('allflex-pricing');
      if (!pricingEl) return;
      var linesEl = pricingEl.querySelector('.allflex-pricing__lines');
      if (!linesEl) return;

      var quantity = (detail && detail.quantity) || 1;
      var perTagPrice = getCurrentPrice();
      if (perTagPrice === null || perTagPrice >= compare) {
        // Remove any stale savings line if our price is at/above compare.
        var stale = linesEl.querySelector('.allflex-pricing__line--cro-savings');
        if (stale) stale.remove();
        return;
      }

      var perTagSave = compare - perTagPrice;
      var totalSave  = perTagSave * quantity;
      var pct = Math.round((perTagSave / compare) * 100);

      var totalRow = linesEl.querySelector('.allflex-pricing__line--total');

      var savingsLine = linesEl.querySelector('.allflex-pricing__line--cro-savings');
      if (!savingsLine) {
        savingsLine = document.createElement('div');
        savingsLine.className = 'allflex-pricing__line allflex-pricing__line--cro-savings';
        if (totalRow) {
          linesEl.insertBefore(savingsLine, totalRow);
        } else {
          linesEl.appendChild(savingsLine);
        }
      }
      savingsLine.innerHTML =
        '<span>You save vs ' + fmt(compare) + ' retail (' + pct + '% off)</span>' +
        '<strong>' + fmt(totalSave) + '</strong>';
    }

    // ---- Hook 1: observe top-price changes (variant-auto-select.js swaps) --
    var priceObserver = new MutationObserver(function () {
      updateStrikethroughBlock();
    });
    priceObserver.observe(observerTarget, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    // ---- Hook 2: pricing:updated event from allflex-pricing component -----
    document.addEventListener('pricing:updated', function (e) {
      // Defer one frame so allflex-pricing has finished writing innerHTML.
      requestAnimationFrame(function () {
        injectBreakdownSavings(e.detail || {});
        // Also re-sync the top strikethrough (price may have changed).
        updateStrikethroughBlock();
      });
    });

    // Initial pass on load — covers the page-load state. The
    // allflex-pricing component fires pricing:updated when it first renders,
    // but on a cold page-load that fire can land BEFORE our listener
    // registers. Synthesise an initial call so the breakdown savings row is
    // visible at the default quantity, not just after the first interaction.
    updateStrikethroughBlock();
    var qtyInput = document.querySelector('input[data-allflex-field="quantity"]');
    var initialQty = qtyInput ? parseInt(qtyInput.value, 10) : 1;
    if (!isFinite(initialQty) || initialQty < 1) initialQty = 1;
    injectBreakdownSavings({ quantity: initialQty });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/* ----- Inline styles for the new breakdown savings line ----- */
(function () {
  if (typeof window === 'undefined') return;
  if (document.getElementById('cro-allflex-dynamic-savings-css')) return;
  var style = document.createElement('style');
  style.id = 'cro-allflex-dynamic-savings-css';
  style.textContent =
    '.allflex-pricing__line--cro-savings{' +
      'color:#c92121;' +
      'font-weight:600;' +
      'background:rgba(231,60,60,0.08);' +
      'border-radius:4px;' +
      'padding:0.35rem 0.6rem;' +
      'margin:0.15rem 0;' +
    '}' +
    '.allflex-pricing__line--cro-savings strong{font-weight:700;color:#c92121;}';
  document.head.appendChild(style);
})();
